import { useRef, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useI18n } from '../../i18n/useI18n';
import { useUi } from '../../context/UiContext';
import { useDismiss } from '../../hooks/useDismiss';
import { anchorFromOffset, daysBetween, isSameDay, monthGrid, weekdayHeads, weekStartsOn } from '../../utils/dates';
import { IconChevronLeft, IconChevronRight } from '../icons/Icons';
import styles from './overlays.module.css';

export function CalendarPopover({ anchorRef }) {
  const { state, actions } = useApp();
  const { lang, t } = useI18n();
  const { close } = useUi();
  const popRef = useRef(null);
  const anchor = anchorFromOffset(state.dayOffset);
  const [month, setMonth] = useState(() => new Date(anchor.getFullYear(), anchor.getMonth(), 1));
  useDismiss(true, () => close('calendarOpen'), [popRef, anchorRef]);

  const today = new Date();
  const start = weekStartsOn(lang);
  const days = monthGrid(month, start);
  const heads = weekdayHeads(lang, start);
  const label = month.toLocaleDateString(lang === 'ar' ? 'ar' : 'en-US', {
    month: 'long',
    year: 'numeric',
    numberingSystem: 'latn',
  });

  return (
    <div className={styles.calendar} ref={popRef} role="dialog" aria-label={t('cal.label')}>
      <div className={styles.calHead}>
        <button
          type="button"
          className={styles.calNav}
          aria-label={t('cal.prev')}
          data-mirror=""
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
        >
          <IconChevronLeft />
        </button>
        <strong>{label}</strong>
        <button
          type="button"
          className={styles.calNav}
          aria-label={t('cal.next')}
          data-mirror=""
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
        >
          <IconChevronRight />
        </button>
      </div>
      <div className={styles.weekdays}>
        {heads.map((day, index) => (
          <span key={`${day}-${index}`}>{day}</span>
        ))}
      </div>
      <div className={styles.grid}>
        {days.map((date) => {
          const outside = date.getMonth() !== month.getMonth();
          const selected = isSameDay(date, anchor);
          const isToday = isSameDay(date, today);
          return (
            <button
              key={date.toISOString()}
              type="button"
              className={outside ? `${styles.dayBtn} ${styles.outside}` : styles.dayBtn}
              data-today={isToday ? 'true' : 'false'}
              data-selected={selected ? 'true' : 'false'}
              onClick={() => {
                actions.setOffset(daysBetween(new Date(), date));
                close('calendarOpen');
              }}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}